import React, { useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useSelector } from "react-redux";
import { Coordinates } from "@optimusride/client-library/@types/grpc/common";
import { OperatingArea } from "@optimusride/client-library/@types/grpc/site";
import { injectGlobal } from "@emotion/css";
import LocationIcon from "../assets/LocationIcon";
import { RootState } from "../../../store";
import { WHITE } from "../../common/styles/colors";
import { HEADING_FONT, FONT_1_25, FONT_1_125 } from "../../common/styles/fonts";
import { convertLatLng } from "../../common/utils";
import { Stations, StationWithLetterID } from "../../User/userSlice";
import { defaultMarkerCalculator, getAddressString, SVG_BASE } from "../utils";
import { Z_INDEX_MAP_MARKER } from "../../common/styles/z-index";
import { SPACING_2, SPACING_4, SPACING_64 } from "../../common/styles/spacing";
// eslint-disable-next-line import/no-absolute-path
import StationCluster from "/stationCluster1.png";

injectGlobal(`
    .Cluster_Station div {
        position: absolute !important;
        right: calc(${SPACING_2} + ${SPACING_4}) !important;
        bottom: ${SPACING_2} !important;

        font-family: ${HEADING_FONT} !important;
        font-size: ${FONT_1_125} !important;
        color: ${WHITE} !important;
    }

    .Cluster_Station img {
        height: unset !important;
        width: ${SPACING_64} !important;
    }
`);

const MARKER_IMAGE = SVG_BASE + encodeURIComponent(
    renderToStaticMarkup(
        <LocationIcon height={40} width={50} />,
    ),
);

export default (selectedSite?: OperatingArea) => {
    const STATION_ICON = useMemo(() => ({
        anchor: new google.maps.Point(0, 25),
        url: MARKER_IMAGE,
        labelOrigin: new google.maps.Point(25, 18),
    }), []);

    // Creates the station markers with the marker icon and the
    // letter id text
    const createStationMarkerOptions = ({
        latLon, nickname, letterID,
        ...addressDetails
    }: StationWithLetterID) => ({
        latLng: convertLatLng(latLon as Coordinates),
        modalText: `<b>${nickname}</b>\n${getAddressString(addressDetails)}`,
        icon: STATION_ICON,
        label: {
            text: letterID,
            color: WHITE,
            fontWeight: "bold",
            fontFamily: HEADING_FONT,
            fontSize: FONT_1_25,
        },
        zIndex: Z_INDEX_MAP_MARKER,
    });

    const stations = useSelector<RootState, Stations>(
        (state) => state.user.stations,
    );

    const stationList = useMemo(() => (
        selectedSite?.id
            ? stations[selectedSite.id]
            : undefined
    ), [selectedSite, stations]);

    const stationMarkers = useMemo(
        () => (
            stationList?.filter(({ disabled }) => !disabled)
                .map(createStationMarkerOptions) || []
        ),
        [selectedSite, stations],
    );

    const stationMarkersClusterDetails = useMemo(() => ({
        icon: StationCluster,
        className: "Cluster_Station",
        calculator: defaultMarkerCalculator(),
    }), []);

    return {
        stationList,
        stationMarkers,
        stationMarkersClusterDetails,
        stationLoading: !stations,
    };
};
